import {
  regenerateSiteBuilderBlockUuids,
} from "@webdo/site-templates/block-identity";
import type {
  SiteBuilderBlock,
  SiteBuilderContentJson,
} from "@webdo/site-templates/builder";
import type { BuilderStudioBlockSelection } from "./types";

export type BuilderBlockAddressSegment =
  | {
      index: number;
      kind: "blocks";
    }
  | {
      index: number;
      kind: "children";
    }
  | {
      index: number;
      key: string;
      kind: "slot";
    };

export type BuilderBlockAddress = readonly BuilderBlockAddressSegment[];

export type BuilderBlockCollectionAddress =
  | {
      kind: "blocks";
    }
  | {
      kind: "children";
      parentAddress: BuilderBlockAddress;
    }
  | {
      key: string;
      kind: "slot";
      parentAddress: BuilderBlockAddress;
    };

export type BuilderBlockEntry = {
  address: BuilderBlockAddress;
  block: SiteBuilderBlock;
  path: string;
};

export type BuilderBlockDropPosition = "before" | "after";

function cloneDocument(
  document: SiteBuilderContentJson,
): SiteBuilderContentJson {
  return globalThis.structuredClone(document);
}

function createBlockUuid(): string {
  if (
    typeof globalThis.crypto !== "undefined"
    && typeof globalThis.crypto.randomUUID === "function"
  ) {
    return globalThis.crypto.randomUUID();
  }

  return `builder-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function cloneBlockWithFreshUuids(block: SiteBuilderBlock): SiteBuilderBlock {
  return regenerateSiteBuilderBlockUuids(globalThis.structuredClone(block));
}

function ensureBlockUuids(block: SiteBuilderBlock): SiteBuilderBlock {
  const clone = globalThis.structuredClone(block);

  function ensure(candidate: SiteBuilderBlock): void {
    if (!candidate.uuid?.trim()) {
      candidate.uuid = createBlockUuid();
    }

    candidate.children?.forEach(ensure);
    Object.values(candidate.slots ?? {}).forEach((blocks) => {
      blocks.forEach(ensure);
    });
  }

  ensure(clone);

  return clone;
}

function encodedIdentity(block: SiteBuilderBlock, index: number): string {
  return block.uuid?.trim()
    ? `uuid:${encodeURIComponent(block.uuid.trim())}`
    : `index:${index}:${encodeURIComponent(block.type)}`;
}

function childPath(
  parentPath: string,
  segment: BuilderBlockAddressSegment,
  block: SiteBuilderBlock,
): string {
  if (segment.kind === "slot") {
    return `${parentPath}/slots/${encodeURIComponent(segment.key)}/${encodedIdentity(block, segment.index)}`;
  }

  return `${parentPath}/children/${encodedIdentity(block, segment.index)}`;
}

function visitBlocks(
  blocks: readonly SiteBuilderBlock[],
  parentAddress: BuilderBlockAddress,
  parentPath: string | null,
  kind: "blocks" | "children" | "slot",
  slotKey: string | null,
  entries: BuilderBlockEntry[],
): void {
  blocks.forEach((block, index) => {
    const segment: BuilderBlockAddressSegment = kind === "slot"
      ? {
          index,
          key: slotKey ?? "",
          kind,
        }
      : {
          index,
          kind,
        };
    const address = [...parentAddress, segment];
    const path = parentPath === null
      ? `/blocks/${encodedIdentity(block, index)}`
      : childPath(parentPath, segment, block);

    entries.push({
      address,
      block,
      path,
    });

    if (block.children) {
      visitBlocks(
        block.children,
        address,
        path,
        "children",
        null,
        entries,
      );
    }

    Object.entries(block.slots ?? {}).forEach(([key, slotBlocks]) => {
      visitBlocks(slotBlocks, address, path, "slot", key, entries);
    });
  });
}

export function builderBlockEntries(
  document: SiteBuilderContentJson,
): BuilderBlockEntry[] {
  const entries: BuilderBlockEntry[] = [];

  visitBlocks(
    document.blocks ?? [],
    [],
    null,
    "blocks",
    null,
    entries,
  );

  return entries;
}

export function findBuilderBlockAddress(
  document: SiteBuilderContentJson,
  selection: BuilderStudioBlockSelection,
): BuilderBlockAddress | null {
  const entries = builderBlockEntries(document);
  const uuid = selection.blockUuid?.trim();

  if (uuid) {
    const entry = entries.find((candidate) => candidate.block.uuid === uuid);

    if (entry) {
      return entry.address;
    }
  }

  if (selection.blockPath) {
    const entry = entries.find(
      (candidate) => candidate.path === selection.blockPath,
    );

    if (entry) {
      return entry.address;
    }
  }

  if (uuid || selection.blockPath) {
    return null;
  }

  const root = entries.find(
    (candidate) =>
      candidate.address.length === 1
      && candidate.address[0]?.index === selection.rootIndex,
  );

  return root?.address ?? null;
}

function selectionForBuilderBlockEntry(
  entry: BuilderBlockEntry,
  previous: BuilderStudioBlockSelection | null,
): BuilderStudioBlockSelection {
  return {
    blockPath: entry.path,
    blockType: entry.block.type,
    blockUuid: entry.block.uuid ?? null,
    part: previous?.part ?? "root",
    rootIndex: entry.address[0]?.index ?? 0,
    ...(previous?.runtimeTarget
      ? { runtimeTarget: previous.runtimeTarget }
      : {}),
    slot: previous?.slot ?? null,
  };
}

/**
 * Keeps the controlled selection attached to the same block across history
 * snapshots. UUID is the stable identity, while blockPath supports legacy
 * documents that do not yet carry UUIDs. If the selected block disappeared,
 * prefer its closest surviving ancestor and finally Shop-style root clamping.
 */
export function resolveBuilderBlockSelection(
  document: SiteBuilderContentJson,
  selection: BuilderStudioBlockSelection | null,
): BuilderStudioBlockSelection | null {
  if (!selection) {
    return null;
  }

  const entries = builderBlockEntries(document);
  const uuid = selection.blockUuid?.trim();
  const path = selection.blockPath?.trim();
  const exactEntry = (
    uuid
      ? entries.find((entry) => entry.block.uuid === uuid)
      : null
  ) ?? (
    path
      ? entries.find((entry) => entry.path === path)
      : null
  );

  if (exactEntry) {
    return selectionForBuilderBlockEntry(exactEntry, selection);
  }

  const ancestorEntry = path
    ? entries
        .filter((entry) => path.startsWith(`${entry.path}/`))
        .sort((left, right) => right.path.length - left.path.length)[0]
    : null;

  if (ancestorEntry) {
    return selectionForBuilderBlockEntry(ancestorEntry, null);
  }

  const rootBlocks = document.blocks ?? [];

  if (rootBlocks.length === 0) {
    return null;
  }

  const rootIndex = Math.max(
    0,
    Math.min(selection.rootIndex, rootBlocks.length - 1),
  );
  const rootEntry = entries.find((entry) => (
    entry.address.length === 1
    && entry.address[0]?.index === rootIndex
  ));

  return rootEntry
    ? selectionForBuilderBlockEntry(rootEntry, null)
    : null;
}

function mutableCollectionAtAddress(
  document: SiteBuilderContentJson,
  address: BuilderBlockAddress,
): SiteBuilderBlock[] | null {
  let collection = document.blocks ?? null;

  for (let depth = 0; depth < address.length; depth += 1) {
    const segment = address[depth];

    if (!segment || !collection) {
      return null;
    }

    if (depth === address.length - 1) {
      return collection;
    }

    const block = collection[segment.index];
    const next = address[depth + 1];

    if (!block || !next) {
      return null;
    }

    if (next.kind === "children") {
      collection = block.children ?? null;
    } else if (next.kind === "slot") {
      collection = block.slots?.[next.key] ?? null;
    } else {
      return null;
    }
  }

  return collection;
}

function mutableCollectionAtCollectionAddress(
  document: SiteBuilderContentJson,
  address: BuilderBlockCollectionAddress,
): SiteBuilderBlock[] | null {
  if (address.kind === "blocks") {
    document.blocks ??= [];

    return document.blocks;
  }

  const parent = builderBlockAtAddress(document, address.parentAddress);

  if (!parent) {
    return null;
  }

  if (address.kind === "children") {
    parent.children ??= [];

    return parent.children;
  }

  parent.slots ??= {};
  parent.slots[address.key] ??= [];

  return parent.slots[address.key];
}

function collectionAddressForBlockAddress(
  address: BuilderBlockAddress,
): BuilderBlockCollectionAddress | null {
  const segment = address.at(-1);

  if (!segment) {
    return null;
  }

  if (segment.kind === "blocks") {
    return {
      kind: "blocks",
    };
  }

  if (segment.kind === "children") {
    return {
      kind: "children",
      parentAddress: address.slice(0, -1),
    };
  }

  return {
    key: segment.key,
    kind: "slot",
    parentAddress: address.slice(0, -1),
  };
}

function collectionAddressesEqual(
  left: BuilderBlockCollectionAddress,
  right: BuilderBlockCollectionAddress,
): boolean {
  if (left.kind !== right.kind) {
    return false;
  }

  if (left.kind === "blocks" || right.kind === "blocks") {
    return left.kind === right.kind;
  }

  if (
    left.kind === "slot"
    && right.kind === "slot"
    && left.key !== right.key
  ) {
    return false;
  }

  return left.parentAddress.length === right.parentAddress.length
    && left.parentAddress.every((segment, index) => {
      const candidate = right.parentAddress[index];

      return candidate !== undefined
        && segment.kind === candidate.kind
        && segment.index === candidate.index
        && (
          segment.kind !== "slot"
          || (
            candidate.kind === "slot"
            && segment.key === candidate.key
          )
        );
    });
}

function clampedCollectionIndex(
  collection: readonly SiteBuilderBlock[],
  index: number,
): number {
  return Math.max(0, Math.min(collection.length, Math.trunc(index)));
}

export function builderBlockAtAddress(
  document: SiteBuilderContentJson,
  address: BuilderBlockAddress,
): SiteBuilderBlock | null {
  const segment = address.at(-1);
  const collection = mutableCollectionAtAddress(document, address);

  return segment && collection
    ? collection[segment.index] ?? null
    : null;
}

export function updateBuilderBlockAtAddress(
  document: SiteBuilderContentJson,
  address: BuilderBlockAddress,
  update: (
    block: SiteBuilderBlock,
  ) => SiteBuilderBlock,
): SiteBuilderContentJson {
  const next = cloneDocument(document);
  const collection = mutableCollectionAtAddress(next, address);
  const segment = address.at(-1);

  if (!collection || !segment || !collection[segment.index]) {
    return document;
  }

  collection[segment.index] = ensureBlockUuids(
    update(collection[segment.index]),
  );

  return next;
}

export function appendBuilderBlock(
  document: SiteBuilderContentJson,
  block: SiteBuilderBlock,
): SiteBuilderContentJson {
  const next = cloneDocument(document);
  next.blocks = [...(next.blocks ?? []), ensureBlockUuids(block)];

  return next;
}

export function appendBuilderRootContainerWithBlock(
  document: SiteBuilderContentJson,
  container: SiteBuilderBlock,
  slotKey: string,
  block: SiteBuilderBlock,
): SiteBuilderContentJson {
  return appendBuilderBlock(document, {
    ...container,
    slots: {
      ...container.slots,
      [slotKey]: [
        ...(container.slots?.[slotKey] ?? []),
        block,
      ],
    },
  });
}

export function appendBuilderBlocks(
  document: SiteBuilderContentJson,
  blocks: readonly SiteBuilderBlock[],
): SiteBuilderContentJson {
  const next = cloneDocument(document);
  next.blocks = [
    ...(next.blocks ?? []),
    ...blocks.map((block) => cloneBlockWithFreshUuids(block)),
  ];

  return next;
}

export function insertBuilderBlockAfter(
  document: SiteBuilderContentJson,
  address: BuilderBlockAddress | null,
  block: SiteBuilderBlock,
): SiteBuilderContentJson {
  return insertBuilderBlockRelative(document, address, block, "after");
}

export function insertBuilderBlockRelative(
  document: SiteBuilderContentJson,
  address: BuilderBlockAddress | null,
  block: SiteBuilderBlock,
  position: BuilderBlockDropPosition,
): SiteBuilderContentJson {
  if (!address) {
    return appendBuilderBlock(document, block);
  }

  const next = cloneDocument(document);
  const collection = mutableCollectionAtAddress(next, address);
  const segment = address.at(-1);

  if (!collection || !segment) {
    return appendBuilderBlock(document, block);
  }

  collection.splice(
    segment.index + (position === "after" ? 1 : 0),
    0,
    ensureBlockUuids(block),
  );

  return next;
}

export function insertBuilderBlockIntoCollection(
  document: SiteBuilderContentJson,
  collectionAddress: BuilderBlockCollectionAddress,
  index: number,
  block: SiteBuilderBlock,
): SiteBuilderContentJson {
  const next = cloneDocument(document);
  const collection = mutableCollectionAtCollectionAddress(
    next,
    collectionAddress,
  );

  if (!collection) {
    return document;
  }

  collection.splice(
    clampedCollectionIndex(collection, index),
    0,
    ensureBlockUuids(block),
  );

  return next;
}

/**
 * Replaces one neutral block collection without changing the identity of
 * existing descendants. This is used by compound editor operations such as a
 * left/right line drop, where two siblings become editable Column blocks in a
 * single history snapshot.
 */
export function replaceBuilderBlockCollection(
  document: SiteBuilderContentJson,
  collectionAddress: BuilderBlockCollectionAddress,
  blocks: readonly SiteBuilderBlock[],
): SiteBuilderContentJson {
  const next = cloneDocument(document);
  const collection = mutableCollectionAtCollectionAddress(
    next,
    collectionAddress,
  );

  if (!collection) {
    return document;
  }

  collection.splice(
    0,
    collection.length,
    ...blocks.map((block) => ensureBlockUuids(block)),
  );

  return next;
}

export function spliceBuilderBlocksIntoCollection(
  document: SiteBuilderContentJson,
  collectionAddress: BuilderBlockCollectionAddress,
  index: number,
  blocks: readonly SiteBuilderBlock[],
  replaceCount = 0,
): SiteBuilderContentJson {
  const next = cloneDocument(document);
  const collection = mutableCollectionAtCollectionAddress(
    next,
    collectionAddress,
  );

  if (!collection || blocks.length === 0) {
    return document;
  }

  const insertIndex = clampedCollectionIndex(collection, index);
  const clampedReplaceCount = Math.min(
    Math.max(0, Math.trunc(replaceCount)),
    Math.max(0, collection.length - insertIndex),
  );

  collection.splice(
    insertIndex,
    clampedReplaceCount,
    ...blocks.map(ensureBlockUuids),
  );

  return next;
}

export function duplicateBuilderBlock(
  document: SiteBuilderContentJson,
  address: BuilderBlockAddress,
): SiteBuilderContentJson {
  const next = cloneDocument(document);
  const collection = mutableCollectionAtAddress(next, address);
  const segment = address.at(-1);
  const block = segment ? collection?.[segment.index] : null;

  if (!collection || !segment || !block) {
    return document;
  }

  collection.splice(
    segment.index + 1,
    0,
    cloneBlockWithFreshUuids(block),
  );

  return next;
}

export function deleteBuilderBlock(
  document: SiteBuilderContentJson,
  address: BuilderBlockAddress,
): SiteBuilderContentJson {
  const next = cloneDocument(document);
  const collection = mutableCollectionAtAddress(next, address);
  const segment = address.at(-1);

  if (!collection || !segment || !collection[segment.index]) {
    return document;
  }

  collection.splice(segment.index, 1);

  return next;
}

export function reorderBuilderBlock(
  document: SiteBuilderContentJson,
  address: BuilderBlockAddress,
  direction: -1 | 1,
): SiteBuilderContentJson {
  const next = cloneDocument(document);
  const collection = mutableCollectionAtAddress(next, address);
  const segment = address.at(-1);

  if (!collection || !segment) {
    return document;
  }

  const targetIndex = segment.index + direction;

  if (
    !collection[segment.index]
    || targetIndex < 0
    || targetIndex >= collection.length
  ) {
    return document;
  }

  const [block] = collection.splice(segment.index, 1);
  collection.splice(targetIndex, 0, block);

  return next;
}

function isAncestorAddress(
  ancestor: BuilderBlockAddress,
  descendant: BuilderBlockAddress,
): boolean {
  if (ancestor.length >= descendant.length) {
    return false;
  }

  return ancestor.every((segment, index) => {
    const candidate = descendant[index];

    if (!candidate || segment.kind !== candidate.kind) {
      return false;
    }

    if (segment.index !== candidate.index) {
      return false;
    }

    return segment.kind !== "slot"
      || (candidate.kind === "slot" && segment.key === candidate.key);
  });
}

function addressesEqual(
  left: BuilderBlockAddress,
  right: BuilderBlockAddress,
): boolean {
  return left.length === right.length
    && left.every((segment, index) => {
      const candidate = right[index];

      return candidate !== undefined
        && segment.kind === candidate.kind
        && segment.index === candidate.index
        && (
          segment.kind !== "slot"
          || (
            candidate.kind === "slot"
            && segment.key === candidate.key
          )
        );
    });
}

export function builderBlockCollectionWouldCreateCycle(
  sourceAddress: BuilderBlockAddress,
  targetCollectionAddress: BuilderBlockCollectionAddress,
): boolean {
  if (targetCollectionAddress.kind === "blocks") {
    return false;
  }

  return addressesEqual(
    sourceAddress,
    targetCollectionAddress.parentAddress,
  ) || isAncestorAddress(
    sourceAddress,
    targetCollectionAddress.parentAddress,
  );
}

/**
 * Rebase an address after removing one item from a sibling collection.
 *
 * This is deliberately index-aware rather than UUID-only: imported/legacy
 * documents can still contain identity-less blocks, and moving an earlier
 * root must not make a later identity-less target disappear.
 */
function rebaseAddressAfterRemoval(
  address: BuilderBlockAddress,
  removedAddress: BuilderBlockAddress,
): BuilderBlockAddress {
  const removedSegment = removedAddress.at(-1);
  const removedParent = removedAddress.slice(0, -1);
  const candidate = address[removedParent.length];

  if (
    !removedSegment
    || !candidate
    || !addressesEqual(
      address.slice(0, removedParent.length),
      removedParent,
    )
    || removedSegment.kind !== candidate.kind
    || (
      removedSegment.kind === "slot"
      && (
        candidate.kind !== "slot"
        || removedSegment.key !== candidate.key
      )
    )
    || removedSegment.index >= candidate.index
  ) {
    return address;
  }

  return address.map((segment, index) => (
    index === removedParent.length
      ? {
          ...segment,
          index: segment.index - 1,
        }
      : segment
  ));
}

/**
 * Moves a block to a concrete neutral collection, including an empty slot.
 *
 * The exact target address is rebased after source removal. Address-first
 * resolution remains deterministic even for imported documents that contain
 * duplicate UUIDs, while still handling every shifted sibling/root index.
 */
export function moveBuilderBlockIntoCollection(
  document: SiteBuilderContentJson,
  sourceAddress: BuilderBlockAddress,
  targetCollectionAddress: BuilderBlockCollectionAddress,
  targetIndex: number,
): SiteBuilderContentJson {
  const targetParentAddress = targetCollectionAddress.kind === "blocks"
    ? null
    : targetCollectionAddress.parentAddress;

  if (
    builderBlockCollectionWouldCreateCycle(
      sourceAddress,
      targetCollectionAddress,
    )
  ) {
    return document;
  }

  const sourceCollectionAddress = collectionAddressForBlockAddress(
    sourceAddress,
  );
  const sameCollection = sourceCollectionAddress
    ? collectionAddressesEqual(
        sourceCollectionAddress,
        targetCollectionAddress,
      )
    : false;
  const sourceSegment = sourceAddress.at(-1);
  const next = cloneDocument(document);
  const sourceCollection = mutableCollectionAtAddress(next, sourceAddress);

  if (!sourceCollection || !sourceSegment) {
    return document;
  }

  const [block] = sourceCollection.splice(sourceSegment.index, 1);

  if (!block) {
    return document;
  }

  let resolvedTargetCollectionAddress = targetCollectionAddress;

  if (targetCollectionAddress.kind !== "blocks") {
    const resolvedParentAddress = targetParentAddress
      ? rebaseAddressAfterRemoval(targetParentAddress, sourceAddress)
      : null;

    if (!resolvedParentAddress) {
      return document;
    }

    resolvedTargetCollectionAddress = targetCollectionAddress.kind
      === "children"
      ? {
          kind: "children",
          parentAddress: resolvedParentAddress,
        }
      : {
          key: targetCollectionAddress.key,
          kind: "slot",
          parentAddress: resolvedParentAddress,
        };
  }

  const targetCollection = mutableCollectionAtCollectionAddress(
    next,
    resolvedTargetCollectionAddress,
  );

  if (!targetCollection) {
    return document;
  }

  const adjustedIndex = sameCollection
    && sourceSegment.index < targetIndex
    ? targetIndex - 1
    : targetIndex;

  targetCollection.splice(
    clampedCollectionIndex(targetCollection, adjustedIndex),
    0,
    block,
  );

  return next;
}

export function moveBuilderBlock(
  document: SiteBuilderContentJson,
  sourceAddress: BuilderBlockAddress,
  targetAddress: BuilderBlockAddress,
  position: BuilderBlockDropPosition,
): SiteBuilderContentJson {
  if (
    isAncestorAddress(sourceAddress, targetAddress)
    || (
      sourceAddress.length === targetAddress.length
      && sourceAddress.every(
        (segment, index) =>
          segment.kind === targetAddress[index]?.kind
          && segment.index === targetAddress[index]?.index,
      )
    )
  ) {
    return document;
  }

  const next = cloneDocument(document);
  const sourceCollection = mutableCollectionAtAddress(next, sourceAddress);
  const sourceSegment = sourceAddress.at(-1);

  if (!sourceCollection || !sourceSegment) {
    return document;
  }

  const [block] = sourceCollection.splice(sourceSegment.index, 1);

  if (!block) {
    return document;
  }

  const sourceDepth = sourceAddress.length - 1;
  const sourceParent = sourceAddress.slice(0, -1);
  const targetSharesSourceParent = sourceParent.every((segment, index) => {
    const candidate = targetAddress[index];

    if (
      !candidate
      || segment.kind !== candidate.kind
      || segment.index !== candidate.index
    ) {
      return false;
    }

    return segment.kind !== "slot"
      || (candidate.kind === "slot" && segment.key === candidate.key);
  });
  const adjustedTargetAddress = targetAddress.map((segment, index) => {
    if (
      index === sourceDepth
      && targetSharesSourceParent
      && sourceSegment.kind === segment.kind
      && (
        sourceSegment.kind !== "slot"
        || (
          segment.kind === "slot"
          && sourceSegment.key === segment.key
        )
      )
      && sourceSegment.index < segment.index
    ) {
      return {
        ...segment,
        index: segment.index - 1,
      };
    }

    return segment;
  });
  const targetCollection = mutableCollectionAtAddress(
    next,
    adjustedTargetAddress,
  );
  const targetSegment = adjustedTargetAddress.at(-1);

  if (!targetCollection || !targetSegment) {
    return document;
  }

  targetCollection.splice(
    targetSegment.index + (position === "after" ? 1 : 0),
    0,
    block,
  );

  return next;
}

export function builderBlockSiblingPosition(
  document: SiteBuilderContentJson,
  address: BuilderBlockAddress,
): {
  canMoveDown: boolean;
  canMoveUp: boolean;
} {
  const collection = mutableCollectionAtAddress(document, address);
  const segment = address.at(-1);

  if (!collection || !segment) {
    return {
      canMoveDown: false,
      canMoveUp: false,
    };
  }

  return {
    canMoveDown: segment.index < collection.length - 1,
    canMoveUp: segment.index > 0,
  };
}
